import React from 'react';
import { Popover, Tag, Typography } from 'antd';
import { IndexFund } from 'state';

const { Text } = Typography;

/** Minimal session shape we read for labelling; sessions have no plain `name`, so klass/trainer. */
export type SessionLite = {
  id: number;
  klass_name?: string;
  trainer_name?: string;
};

const label = (id: number, byId: Record<number, SessionLite>): string => {
  const session = byId[id];
  const name = session?.klass_name || session?.trainer_name;
  return name ? `#${id} · ${name}` : `#${id}`;
};

type Props = {
  fund: IndexFund;
  sessionsById: Record<number, SessionLite>;
};

/**
 * Compact usage indicator: green (playing) / orange (paused) count chips that expand to a popover
 * listing the sessions — replacing the old inline comma-separated id dump. Session names are
 * best-effort (resolved from loaded admin sessions), falling back to `#id`.
 */
const UsageCell: React.FC<Props> = ({ fund, sessionsById }) => {
  const playing = fund.in_playing_sessions || [];
  const paused = fund.in_paused_sessions || [];

  if (playing.length + paused.length === 0) {
    return <Text type="secondary">—</Text>;
  }

  const group = (heading: string, ids: number[], color: string) =>
    ids.length ? (
      <div style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 12 }}>
          {heading} ({ids.length})
        </Text>
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}
        >
          {ids.map(id => (
            <Tag color={color} key={id} style={{ marginRight: 0 }}>
              {label(id, sessionsById)}
            </Tag>
          ))}
        </div>
      </div>
    ) : null;

  const content = (
    <div style={{ maxWidth: 340, maxHeight: 260, overflowY: 'auto' }}>
      {group('Playing', playing, 'green')}
      {group('Paused', paused, 'orange')}
    </div>
  );

  return (
    <Popover content={content} title="Sessions using this fund">
      <span style={{ whiteSpace: 'nowrap', cursor: 'default' }}>
        {playing.length > 0 && (
          <Tag color="green" style={{ marginRight: 4 }}>
            ▶ {playing.length}
          </Tag>
        )}
        {paused.length > 0 && (
          <Tag color="orange" style={{ marginRight: 0 }}>
            ⏸ {paused.length}
          </Tag>
        )}
      </span>
    </Popover>
  );
};

export default UsageCell;
